import type { TranslationLocale } from "@hightouch/lib/cep/localization/translation-state";
import { metaTemplateLanguageForLocale } from "@hightouch/lib/cep/types/whatsapp-template-language";

import type { LocaleRules } from "./locale-rules";

/** Whether Meta has a template language for the locale. */
export function isWhatsAppLocale(locale: Pick<TranslationLocale, "value">) {
  return metaTemplateLanguageForLocale(locale.value) !== null;
}

/**
 * Why a WhatsApp template can't take this locale, or null when it can. Each
 * locale is its own template in one Meta language, so two locales can't share
 * one. A selected locale is never blocked, so it can always be cleared.
 */
export function whatsAppLocaleBlockedReason({
  locale,
  selectedLocales,
}: {
  locale: TranslationLocale;
  selectedLocales: readonly TranslationLocale[];
}): string | null {
  if (selectedLocales.some((selected) => selected.id === locale.id)) {
    return null;
  }
  const language = metaTemplateLanguageForLocale(locale.value);
  if (language === null) return "WhatsApp doesn't support this locale.";
  const clash = selectedLocales.find(
    (selected) => metaTemplateLanguageForLocale(selected.value) === language,
  );
  return clash === undefined
    ? null
    : `Uses the same WhatsApp language as ${clash.value}`;
}

// A WhatsApp template can only be written in a language Meta has.
export const WHATSAPP_LOCALE_RULES: LocaleRules = {
  canBeDefault: isWhatsAppLocale,
  blockedReason: whatsAppLocaleBlockedReason,
};
